// The DRAFT tab (R1317, R1888; ui-map-interface.md §12 "The DRAFT tab"):
// the two shelves of the pattern library, DRAFT · PRINTS and DRAFT ·
// MASTERS, and the draft form of DRAFT · INVENTION (R701, R1194, R1911,
// R1922).
//
// A shelf is a pure function of the state and of the few readers the
// overlay hands it. The draft form keeps the one state of the tab that
// no frame states: the hull, the module tiers and the two typed systems
// the player picked and has not sent yet. The overlay holds one form and
// sends what it reads.

import { Building, JobKind, ModuleFamily, ShipClass } from '../../gen/proto/aquaphobia';
import type { Blueprint } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { type GameState, ZERO_RES, costText, dur } from '../../state';
import type { SceneLook } from '../../render/scene-look';
import { buttonHtml, type CardEnv } from '../cards/card';
import { shipMarkSvg } from '../cards/marks';
import { type Choice, valueChoice } from '../choice/choice';
// The draft form's two prices are a hand mirror of the server's two sums,
// pinned to its own golden vectors (R700).
import { bonusRange, draftCost, fitCost, gradeOdds, redraftCost } from '../fit';
import { esc } from '../esc';
import { termButton, termsHtml } from '../glossary';
import { buildRowHtml, rowIcon } from './buildrow';
import { emptyGoHtml } from './howfold';
import { libraryPress, libraryPressHtml } from './library';
import { fitStrip, moduleGradeWord, moduleLinesInGrade, moduleWithGrade } from './markup';

/** What a shelf reads beside the state. */
export interface ShelfCtx {
  env: CardEnv;
  /** The reader's look, whose ship icon set draws the hull marks. */
  look?: SceneLook;
  /** The design whose ⋯ stands open, or 0 (R1888). */
  moreOpen: number;
  /** The overlay's arm gate (R696): INSTALL and SCRAP draw their armed face. */
  armed: (key: string) => boolean;
}

/**
 * DRAFT · PRINTS (`masters` false): the printed copies, the one shelf a
 * player installs from and sells from (R1317, R1888). DRAFT · MASTERS
 * (`masters` true): the inventions, which print and redraft (R1317).
 */
export function libraryShelf(s: GameState, masters: boolean, ctx: ShelfCtx): string {
  const e = s.empire;
  if (!e) return '';
  // The design library (v6): masters print, prints build, basics are the
  // floor. Masters and copies hold separate libraries (numbers.md §17):
  // an invention and a print do different jobs, so each has its own
  // pill, heading and count (R1317). Basic designs appear on neither:
  // they are the floor, and there is nothing to do to them.
  const shelf = e.blueprints.filter((b) => masters ? b.master : !b.basic && !b.master);
  // A spent print still on the shelf holds no copy slot (R217): the
  // library keeps it only until the work that paid for its last run is
  // laid down. Its row stays, because the player can still read it, but
  // the count has to say what the server's gate says.
  const held = masters ? shelf.length : shelf.filter((b) => b.runs > 0).length;
  // The shelf word is a term of the glossary, and an empty shelf says
  // what to do next (R2176): PRINTS with no master yet points at
  // INVENTION, with masters at the PRINT press of MASTERS.
  const html = `<h3>${S.prints_shelf_head({
    shelf: masters ? termButton('master', S.shelf_masters) : termButton('print', S.shelf_copies),
    held, slots: masters ? e.masterSlots : e.copySlots,
  })}</h3>`;
  if (shelf.length === 0) {
    if (masters) return html + emptyGoHtml(termsHtml(S.prints_no_masters), 'DRAFT', 'INVENTION');
    const anyMaster = e.blueprints.some((b) => b.master);
    return html + (anyMaster ? emptyGoHtml(termsHtml(S.prints_no_copies), 'DRAFT', 'MASTERS')
      : emptyGoHtml(termsHtml(S.prints_no_copies_first), 'DRAFT', 'INVENTION'));
  }
  return html + printRows(s, shelf, ctx);
}

/**
 * A loose print: an unspent copy that no hull slot holds. It is the one
 * print a player may install or sell, so DRAFT · PRINTS and the auction's
 * ADD OFFER page ask the same question.
 */
export function loosePrint(s: GameState, bp: Blueprint): boolean {
  return !bp.basic && !bp.master && bp.runs > 0 && s.installed(bp.hull) !== bp.id;
}

/**
 * One shelf of the pattern library, in the specification row of BUILD
 * (R1888, `overlay/library.ts`): the hull mark, the name and the class
 * on line 1, the runs and the fit strip on line 2 (R1468, R1477), the
 * cost of one hull built to the design over its weight, then one press
 * and ⋯. ⋯ opens the price and SELL of a loose print, the REDRAFT rows
 * of a master and SCRAP under the row. Basic designs never appear:
 * they are the floor, and there is nothing to install, sell or scrap
 * about them.
 */
export function printRows(s: GameState, shelf: readonly Blueprint[], ctx: ShelfCtx): string {
  if (!s.empire) return '';
  const env = ctx.env;
  let html = '';
  for (const bp of shelf) {
    const spec = s.ship(bp.hull);
    // The print that stands in this class's slot, or 0 for the default.
    const slot = s.installed(bp.hull);
    const press = libraryPress(bp, slot);
    const open = ctx.moreOpen === bp.id;
    const kind = bp.master ? S.print_master : S.print_runs({ n: bp.runs });
    const fit = fitStrip(s.catalog, { weapType: bp.weapType, shieldType: bp.shieldType, fit: bp.modules });
    // The title of the name holds what line 2 has no room for: the
    // inventor of a print and each module with its rolled effect and
    // its grade (R1194).
    const title = [
      bp.inventor && !bp.master ? S.print_inventor({ name: esc(bp.inventor) }) : '',
      bp.modules.map(moduleWithGrade).join(' · ') || S.print_no_modules,
    ].filter(Boolean).join(' · ');
    html += buildRowHtml({
      icon: shipMarkSvg(bp.hull, ctx.look),
      name: `<span class="name" title="${title}">${esc(bp.name)}</span>`,
      line2: [kind, S.yard_weight({ weight: spec?.weight ?? 0 })].join(' · '),
      line3: fit,
      side: `<span class="cost">${costText(bp.cost)}</span>`
        + `<span class="time">${dur(s.buildSec(JobKind.JOB_KIND_SHIP, spec?.timeSec ?? 0))}</span>`,
      press: libraryPressHtml(press, {
        id: bp.id, name: bp.name, printWord: S.print_btn({ runs: s.rules.standardPrintRuns }),
        // An install over an occupied slot throws the print that stands
        // there away, so it asks for a second press (R696). An empty
        // slot destroys nothing and keeps its one press.
        overSlot: slot !== 0, installArmed: ctx.armed(`install:${bp.id}`), moreOpen: open,
      }),
      cls: `lib withmore${press === 'inuse' ? ' printed' : ''}`,
    });
    if (!open) continue;
    // ⋯ of the row: the price and SELL of a loose print, then SCRAP.
    // SCRAP throws the design away for good, so it asks for a second
    // press (R696).
    const grid: string[] = [];
    if (loosePrint(s, bp)) {
      grid.push(`<input id="price-${bp.id}" class="lib-price" placeholder="¤" size="6" inputmode="numeric">`);
      grid.push(buttonHtml({ act: 'sell', id: bp.id, label: S.btn_sell, face: 'quiet' }, env));
    }
    grid.push(buttonHtml({
      act: 'scrap', id: bp.id, face: 'quiet', arm: true, danger: true, armed: ctx.armed(`scrap:${bp.id}`),
      label: S.btn_scrap, armLabel: S.scrap_armed,
    }, env));
    html += `<div class="fl-grid">${grid.join('')}</div>`;
    if (bp.master) html += redraftRows(s, bp);
  }
  return html;
}

/**
 * One row for each slot of a master, in ⋯ of the master's row, with the
 * REDRAFT press and its price (numbers.md §6, R1194). A redraft rolls
 * that slot again: a higher roll improves the master in place, and an
 * equal or lower roll is discarded (R1884). The master stays either way,
 * so the control takes one press. The price is the hand mirror of
 * `RedraftCost` (`ui/fit.ts`), off the catalog's share and the slot's
 * own charge. The row is a specification row: the module in the colour
 * of its grade (R1570), the slot on line 1 and the roll on line 2
 * (R2739), and the price in the cost column. The press
 * reaches the server even when the stores fall short, and the server
 * refuses it by name.
 */
function redraftRows(s: GameState, bp: Blueprint): string {
  const hull = s.ship(bp.hull);
  const share = s.catalog?.redraftCostShare ?? 0;
  if (!hull || share <= 0) return '';
  let html = '';
  // The Weapon slot of a hull with no gun takes no redraft (R1911): its
  // press keeps the locked face and says why, and the server refuses it.
  const unarmed = hull.unarmed ? S.invention_unarmed({ hull: esc(hull.name) }) : '';
  bp.modules.forEach((m, slot) => {
    const spec = s.module(m.family, m.tier);
    if (!spec) return;
    const cost = redraftCost(hull.cost ?? ZERO_RES, spec, share);
    const afford = s.canAfford(cost);
    const locked = unarmed !== '' && m.family === ModuleFamily.MODULE_FAMILY_WEAPON;
    // The slot on line 1 and the roll on line 2 (R2739): one line cut the
    // roll on a telephone.
    const lines = moduleLinesInGrade(m, spec.name);
    html += buildRowHtml({
      icon: rowIcon('module'),
      name: lines.slot,
      line2: lines.roll,
      side: `<span class="cost${afford ? '' : ' short'}">${costText(cost)}</span>`,
      press: `<button class="bp ${locked ? 'lock' : afford ? 'ready' : 'short'}" data-act="redraft"`
        + ` data-id="${bp.id}" data-n="${slot}" title="${locked ? unarmed : S.redraft_title}">`
        + `<span class="w">${S.btn_redraft}</span></button>`,
      cls: 'redraft',
    });
  });
  return html;
}

/** The sentence that a hull with no gun refuses a weapon system with
 * (R1911), or '' for a hull with a gun. The catalog row carries the fact. */
export function unarmedWhy(s: GameState, hull: ShipClass): string {
  const spec = s.ship(hull);
  return spec?.unarmed ? S.invention_unarmed({ hull: spec.name }) : '';
}

/** The Ship Design level that opens the typed weapon or shield system
 *  (R1922, `CatalogRules`). */
function typeGate(s: GameState, weapon: boolean): number {
  return weapon ? s.rules.weapTypeShipDesign : s.rules.shieldTypeShipDesign;
}

/**
 * The draft form of DRAFT · INVENTION (2026-08-22, R701): the picks the
 * player has made and not sent. Inventing is where a pattern comes from
 * and printing is what makes it buildable, so the draft has a shelf of
 * its own rather than sitting under the print list.
 */
export class DraftForm {
  /** The hull of the draft; 0 reads as the Cutlass. */
  hull: ShipClass = ShipClass.SHIP_CLASS_CUTLASS;
  /** Module tiers chosen for the current draft, keyed by family; at most one tier per family. */
  readonly modules = new Map<number, number>();
  /** The type pickers of the draft form (numbers.md §17); 0 = untyped. */
  weapType = 0;
  shieldType = 0;

  /** The hull the form draws and checks. */
  private hullNow(): ShipClass {
    return this.hull || ShipClass.SHIP_CLASS_CUTLASS;
  }

  /** The tab body of DRAFT · INVENTION. */
  html(s: GameState): string {
    const e = s.empire;
    if (!e) return '';
    let html = `<h3>${S.invention_head}</h3>`;
    if (s.level(Building.BUILDING_SHIP_DESIGN) === 0) {
      // The roll rule is a fact of the DRAFT fold (R1895).
      return html + emptyGoHtml(S.invention_empty, 'BUILD', 'BASE', { kind: 'building', id: Building.BUILDING_SHIP_DESIGN });
    }
    const hull = this.hullNow();
    // How many families this office may fit, and how many are picked. The
    // ceiling is the empire frame's own figure (R700); the client keeps
    // no ladder of its own.
    html += `<div class="row"><span class="muted">${S.invention_slots({
      used: this.modules.size, slots: e.moduleSlots,
    })}</span></div>`;
    // The hull is a saved pick of the draft: the chooser of the kit
    // gives its form from the count of hulls the tree opened (R2011).
    const hulls = (s.catalog?.ships ?? []).filter((sp) => sp.tech === 0 || s.hasTech(sp.tech));
    html += `<div class="row chrow"><span class="muted">${S.invention_hull}</span>`
      + valueChoice({
        list: hulls.map((sp) => ({ id: String(sp.id), label: sp.name })), chosen: String(hull),
        act: 'invhull', label: S.invention_hull, uid: 'invhull',
      })
      + `</div>`;
    html += this.moduleRows(s, hull);
    html += this.fitHtml(s, hull);
    html += `<div class="acts invacts"><button class="primary" data-act="inventsubmit" data-id="${hull}">${S.invention_submit}</button></div>`;
    return html;
  }

  /**
   * The module rows of the draft form (R701): one row per family the server
   * ships, in the catalog's own order, with the three tiers beside it.
   *
   * The row order, the family names and the gates are ALL the catalog's.
   * The client kept a mirror of each until R700 put the module table on the
   * wire; it keeps none now.
   *
   * A tier the empire cannot reach keeps the `locked` look but is NOT
   * `disabled`, because a disabled button dispatches no pointer event: the
   * old form simply went silent, and the player never learnt which gate
   * held the tier shut. The press now reaches the handler, which refuses it
   * by name.
   */
  private moduleRows(s: GameState, hull: ShipClass): string {
    const mods = s.catalog?.modules ?? [];
    const office = s.level(Building.BUILDING_SHIP_DESIGN);
    // A hull with no gun takes no weapon system (R1911): the catalog row
    // states it, and the Weapon tiers keep the locked face.
    const unarmed = unarmedWhy(s, hull);
    // Grouped in first-seen order, so the shelf reads the way the server
    // listed it. Nothing here sorts.
    const families: ModuleFamily[] = [];
    for (const m of mods) if (!families.includes(m.family)) families.push(m.family);

    let html = '';
    for (const fam of families) {
      const name = mods.find((m) => m.family === fam)?.name ?? '';
      // Each tier is a pill whose id names the family and the tier,
      // "family:tier" (R2011). A locked tier tells: it takes the press.
      const tierList: Choice[] = [1, 2, 3].map((tier) => {
        const label = S.module_tiers[tier - 1];
        const id = `${fam}:${tier}`;
        if (unarmed && fam === ModuleFamily.MODULE_FAMILY_WEAPON) {
          return { id, label, locked: true, tell: true, title: unarmed };
        }
        const spec = s.module(fam, tier);
        const gate = spec?.tech ?? 0;
        const locked = gate !== 0 && !s.hasTech(gate);
        // What this tier is worth before the roll, and the gate that holds
        // it shut when one does.
        const node = locked ? s.tech(gate) : undefined;
        const title = `${S.module_effects[fam - 1] ?? ''} `
          + S.info_module_worth({ pct: ((spec?.effectPct ?? 0) / 10).toFixed(1) })
          + (node ? ` · ${S.why_needs_tech({
            tech: S.info_module_gate({ code: node.code, tech: node.name }),
          })}` : '');
        return { id, label, locked, tell: true, title };
      });
      const picked = this.modules.get(fam);
      const tiers = valueChoice({
        list: tierList, chosen: picked === undefined ? '' : `${fam}:${picked}`, act: 'invmod', label: name,
        uid: `invmod-${fam}`,
      });
      html += `<div class="row chrow"><span class="muted">`
        + `<button class="info" data-act="info" data-kind="module" data-id="${fam}"`
        + ` title="${S.info_btn_module_title}">${esc(name)}</button>`
        // A design carries each family once (R1194): a family on the draft
        // says so, and a press on another tier swaps the tier.
        + (this.modules.has(fam) ? ` <span class="lvl">${S.invention_family_taken}</span>` : '')
        + `</span>${tiers}</div>`;
      // The typed system (§17) stands under its own family, and only while
      // a module of that family is on the draft.
      if (this.modules.has(fam)) html += this.typeRow(s, fam, office);
    }
    return html;
  }

  /**
   * The weapon type row under the Weapon family, or the shield type row
   * under the Shields family (numbers.md §17). Both gates ride the pack,
   * so the tooltips name the level this server asks for (R355). The row
   * offers the three types and no standard system (R1922); the three
   * stand on one line, and a long name wraps inside its button.
   */
  private typeRow(s: GameState, fam: ModuleFamily, office: number): string {
    const weapon = fam === ModuleFamily.MODULE_FAMILY_WEAPON;
    if (!weapon && fam !== ModuleFamily.MODULE_FAMILY_SHIELDS) return '';
    const gate = typeGate(s, weapon);
    const needs = S.invention_needs_sd({ level: gate });
    const names = (weapon ? s.catalog?.weaponTypes : s.catalog?.shieldTypes) ?? [];
    const chosen = this.typeOf(s, fam);
    const act = weapon ? 'invwt' : 'invst';
    const locked = office < gate;
    const label = weapon ? S.invention_weapon : S.invention_shield;
    // The first name is the standard system, which the row does not offer.
    const list: Choice[] = names.slice(1).map((n, i) => ({
      id: String(i + 1), label: n, locked, title: locked ? needs : undefined,
    }));
    return `<div class="row chrow"><span class="muted">${label}</span><span class="ptypes">`
      + valueChoice({ list, chosen: String(chosen), act, label, uid: act })
      + `</span></div>`;
  }

  /**
   * The typed system the draft sends for a family (R1922): 0 while no
   * module of the family is on the draft or the office holds the type
   * shut, else the pick, and the first type while the player picked none.
   */
  private typeOf(s: GameState, fam: ModuleFamily): number {
    if (!this.modules.has(fam)) return 0;
    const weapon = fam === ModuleFamily.MODULE_FAMILY_WEAPON;
    if (s.level(Building.BUILDING_SHIP_DESIGN) < typeGate(s, weapon)) return 0;
    return (weapon ? this.weapType : this.shieldType) || 1;
  }

  /**
   * What this fit will cost and what it will be worth (R701), above the
   * INVENT DESIGN button.
   *
   * Every figure states a MODULE figure and never a hull figure. A
   * Dampening line says how much the module cuts noise, not what the hull's
   * noise becomes: the hull's real noise comes from the Dampening inversion
   * and its floor (numbers.md §6), and neither dial is on the wire. The two
   * prices are the hand mirror of `ui/fit.ts`, pinned to the server's own
   * golden vectors (R700).
   *
   * The block regenerates with the panel at 4 Hz, so it needs no plumbing
   * of its own.
   */
  private fitHtml(s: GameState, hull: ShipClass): string {
    const cat = s.catalog;
    const hullSpec = s.ship(hull);
    if (!cat || !hullSpec) return '';
    // The picks in catalog order, so the preview reads down the shelf.
    const picks = (cat.modules ?? []).filter((m) => this.modules.get(m.family) === m.tier);

    let html = `<h3>${S.invention_fit_head}</h3>`;
    for (const m of picks) {
      const band = bonusRange(m.effectPct, s.rules.inventionRollMin, s.rules.inventionRollMax);
      html += `<div class="row"><span class="muted">${esc(S.module_effects[m.family - 1] ?? '')}`
        + `</span><span>${S.invention_fit_bonus({
          low: band.low.toFixed(1), high: band.high.toFixed(1),
        })}</span></div>`;
    }
    // The odds of the top grade, for each module (R1194): the draw is
    // skewed to the low end, so the band above is not an even gamble.
    const top = s.rules.moduleGrades[s.rules.moduleGrades.length - 1];
    if (picks.length > 0 && top) {
      const odds = gradeOdds(top.chance);
      html += `<div class="row"><span class="muted">${S.invention_fit_top({
        grade: esc(moduleGradeWord(top.code)),
      })}</span><span>${S.invention_fit_odds({ pct: odds.pct, onein: odds.oneIn })}</span></div>`;
    }
    const draft = draftCost(hullSpec.cost ?? ZERO_RES, hullSpec.draftIsotopes, picks,
      cat.inventionCostShare);
    const afford = s.canAfford(draft);
    html += `<div class="row"><span class="muted">${S.invention_fit_build}</span>`
      + `<span class="cost">${costText(fitCost(hullSpec.cost ?? ZERO_RES, picks))}</span></div>`;
    html += `<div class="row"><span class="muted">${S.invention_fit_draft}</span>`
      + `<span class="cost${afford ? '' : ' short'}">${costText(draft)}</span></div>`;
    html += `<div class="row"><span class="muted">${S.invention_fit_time}</span>`
      + `<span class="time">`
      + `${dur(s.buildSec(JobKind.JOB_KIND_INVENT, cat.inventionTimeSec))}</span></div>`;
    return html;
  }

  /**
   * One press on a module tier of the draft form (R701). It answers the
   * refusal the player hears, or '' when the pick took.
   */
  pick(s: GameState, family: number, tier: number): string {
    // The checks in this order (R701, R1911). The button is no longer
    // `disabled`, so every press lands here and every refusal is
    // spoken: a silent form taught the player nothing.
    // 1. A deselect is always free. No gate and no cap may hold a
    //    family the player is taking OFF the draft.
    if (this.modules.get(family) === tier) {
      this.modules.delete(family);
      // A typed system needs its family on the draft (numbers.md §17).
      if (family === ModuleFamily.MODULE_FAMILY_WEAPON) this.weapType = 0;
      if (family === ModuleFamily.MODULE_FAMILY_SHIELDS) this.shieldType = 0;
      return '';
    }
    // 2. A hull with no gun takes no Weapon module (R1911).
    const unarmed = unarmedWhy(s, this.hullNow());
    if (unarmed && family === ModuleFamily.MODULE_FAMILY_WEAPON) return unarmed;
    // 3. The gate this tier asks for, named by its node.
    const gate = s.module(family, tier)?.tech ?? 0;
    if (gate !== 0 && !s.hasTech(gate)) {
      const node = s.tech(gate);
      return S.refuse_module_gate({
        family: s.module(family, tier)?.name ?? '',
        tier: S.module_tiers[tier - 1] ?? tier,
        code: node?.code ?? '?',
        tech: node?.name ?? S.word_a_doctrine,
      });
    }
    // 4. The slot cap, and only for a family that holds no tier yet.
    //    Changing the tier of a family already on the draft takes no
    //    new slot, so it stays free at the cap.
    const slots = s.empire?.moduleSlots ?? 0;
    if (!this.modules.has(family) && this.modules.size >= slots) return S.refuse_module_slots({ slots });
    this.modules.set(family, tier);
    return '';
  }

  /** A hull pick. A hull with no gun takes no weapon system (R1911), so
   *  the Weapon pick and the typed weapon leave the form. */
  setHull(s: GameState, hull: ShipClass): void {
    this.hull = hull;
    if (unarmedWhy(s, hull)) {
      this.modules.delete(ModuleFamily.MODULE_FAMILY_WEAPON);
      this.weapType = 0;
    }
  }

  /**
   * INVENT DESIGN: the modules the draft sends. A module of a typed family
   * sends one of the three types (R1922), so the two pickers settle on the
   * types the intent carries. The picks stay on the form (R1505): a second
   * press files the same draft again.
   */
  submit(s: GameState): { family: ModuleFamily; tier: number }[] {
    this.weapType = this.typeOf(s, ModuleFamily.MODULE_FAMILY_WEAPON);
    this.shieldType = this.typeOf(s, ModuleFamily.MODULE_FAMILY_SHIELDS);
    return Array.from(this.modules, ([family, tier]) => ({ family, tier }));
  }
}
